// i18n area 'auth' (task #127): sections login, user.
// Split out of the old monolithic dictionaries.ts by scripts/split-dictionaries.mjs;
// since the split, these files are the SOURCE — add and edit keys here. The script
// only re-runs to re-split a merged monolithic dictionaries.ts (see its header).

export const enUS = {
  login: {
    welcome: 'Trantor TVirt v6',
    subtitle: 'Please sign-in to your account',
    username: 'Username',
    usernamePlaceholder: 'Enter your username',
    usernameHint: 'Use user@domain to sign in against a directory realm',
    continue: 'Continue',
    changeUser: 'Not you?',
    password: 'Password',
    forgotPassword: 'Forgot password?',
    submit: 'Login',
    otp: 'One-time code',
    otpSubtitle: 'Enter the code from your authenticator',
    otpHint: 'Six digits from your authenticator app, or one of your recovery codes',
    otpSubmit: 'Verify',
    invalidCredentials: 'Invalid username or password',
    tooManyAttempts: 'Too many attempts — wait a moment and try again',
    realmUnavailable: 'The realm cannot verify credentials right now — the cluster store is unreachable. Log in as a local user (break-glass).',
    genericError: 'Login failed — is the TVirt backend reachable?'
  },
  user: {
    logout: 'Logout',
    password: {
      menu: 'Change password',
      title: 'Change your password',
      current: 'Current password',
      new: 'New password',
      confirm: 'Confirm new password',
      mismatch: 'The passwords do not match',
      error: 'Could not change the password',
      forcedTitle: 'Change your password to continue',
      forcedHint: 'This password was set by an administrator and must be replaced before entering.',
      forcedSubmit: 'Change and enter'
    },
    mfa: {
      title: 'Two-factor authentication',
      subtitle: 'A second factor asked for at every sign-in',
      loadError: 'Could not load the second-factor state',
      enrolled: 'Enrolled',
      notEnrolled: 'Not enrolled',
      enrolledAt: 'Enrolled on',
      codesRemaining: 'Recovery codes left',
      requiredHint: 'An administrator requires a second factor on this account: set one up now — until you do, you can sign in but little else.',
      enroll: 'Set up',
      enrollTitle: 'Set up two-factor authentication',
      enrollHint: 'Scan this code with your authenticator app, then type the code it shows to confirm.',
      secretHint: 'Cannot scan? Type this secret into the app instead:',
      code: 'Code from the app',
      confirm: 'Confirm',
      startError: 'Could not start the enrollment',
      confirmError: 'Could not confirm the enrollment',
      codesTitle: 'Your recovery codes',
      codesWarning: 'These codes are shown only once. Save them now: each one signs you in when your authenticator is unavailable, and each works once.',
      codesAck: 'I have saved my recovery codes',
      codesDone: 'Done',
      copy: 'Copy',
      copied: 'Copied',
      download: 'Download',
      regenerate: 'New recovery codes',
      regenerateTitle: 'Regenerate the recovery codes',
      regenerateHint: 'Every code issued so far stops working the moment the new set is generated.',
      regenerateConfirm: 'Regenerate',
      regenerateError: 'Could not regenerate the recovery codes',
      remove: 'Remove',
      removeTitle: 'Remove the second factor',
      removeConfirm: 'Remove',
      removeHint: 'Your account goes back to password only. You can set a second factor up again at any time.',
      removeRequiredHint:
        'An administrator requires a second factor on this account, so the server will refuse to remove it — ask them to lift the requirement first.',
      removeError: 'Could not remove the second factor'
    },
    profile: {
      menu: 'Go to my profile',
      sessions: 'Active sessions',
      sessionsHint: 'Your open sessions on this node',
      sessionsEmpty: 'No active sessions',
      sessionCreated: 'Signed in',
      sessionExpires: 'Expires',
      revoke: 'Revoke session',
      revokeError: 'Could not revoke the session',
      loadError: 'Could not load the sessions'
    }
  }
}

export const itIT: typeof enUS = {
  login: {
    welcome: 'Trantor TVirt v6',
    subtitle: 'Accedi al tuo account',
    username: 'Nome utente',
    usernamePlaceholder: 'Inserisci il nome utente',
    usernameHint: 'Usa utente@dominio per autenticarti su un realm di directory',
    continue: 'Continua',
    changeUser: 'Non sei tu?',
    password: 'Password',
    forgotPassword: 'Password dimenticata?',
    submit: 'Accedi',
    otp: 'Codice monouso',
    otpSubtitle: 'Inserisci il codice del tuo autenticatore',
    otpHint: 'Sei cifre dall’app di autenticazione, oppure uno dei codici di recupero',
    otpSubmit: 'Verifica',
    invalidCredentials: 'Nome utente o password non validi',
    tooManyAttempts: 'Troppi tentativi — attendi un momento e riprova',
    realmUnavailable: 'Il realm non può verificare le credenziali in questo momento — lo store del cluster non è raggiungibile. Accedi come utente locale (break-glass).',
    genericError: 'Accesso non riuscito — il backend TVirt è raggiungibile?'
  },
  user: {
    logout: 'Esci',
    password: {
      menu: 'Cambia password',
      title: 'Cambia la tua password',
      current: 'Password attuale',
      new: 'Nuova password',
      confirm: 'Conferma nuova password',
      mismatch: 'Le password non coincidono',
      error: 'Impossibile cambiare la password',
      forcedTitle: 'Cambia la password per continuare',
      forcedHint: 'Questa password è stata impostata da un amministratore e va sostituita prima di entrare.',
      forcedSubmit: 'Cambia ed entra'
    },
    mfa: {
      title: 'Autenticazione a due fattori',
      subtitle: 'Un secondo fattore richiesto a ogni accesso',
      loadError: 'Impossibile caricare lo stato del secondo fattore',
      enrolled: 'Attivo',
      notEnrolled: 'Non attivo',
      enrolledAt: 'Attivato il',
      codesRemaining: 'Codici di recupero rimasti',
      requiredHint:
        'Un amministratore richiede il secondo fattore su questo account: configuralo adesso — finché non lo fai puoi accedere, ma poco altro.',
      enroll: 'Configura',
      enrollTitle: 'Configura l’autenticazione a due fattori',
      enrollHint: 'Inquadra questo codice con l’app di autenticazione, poi digita il codice che mostra per confermare.',
      secretHint: 'Non riesci a inquadrarlo? Digita questo segreto nell’app:',
      code: 'Codice dall’app',
      confirm: 'Conferma',
      startError: 'Impossibile avviare la configurazione',
      confirmError: 'Impossibile confermare la configurazione',
      codesTitle: 'I tuoi codici di recupero',
      codesWarning:
        'Questi codici vengono mostrati una volta sola. Salvali adesso: ciascuno ti fa accedere quando l’autenticatore non è disponibile, e vale una volta sola.',
      codesAck: 'Ho salvato i codici di recupero',
      codesDone: 'Fatto',
      copy: 'Copia',
      copied: 'Copiato',
      download: 'Scarica',
      regenerate: 'Nuovi codici di recupero',
      regenerateTitle: 'Rigenera i codici di recupero',
      regenerateHint: 'Tutti i codici emessi finora smettono di funzionare nel momento in cui viene generato il nuovo set.',
      regenerateConfirm: 'Rigenera',
      regenerateError: 'Impossibile rigenerare i codici di recupero',
      remove: 'Rimuovi',
      removeTitle: 'Rimuovi il secondo fattore',
      removeConfirm: 'Rimuovi',
      removeHint: 'L’account torna alla sola password. Puoi riconfigurare il secondo fattore quando vuoi.',
      removeRequiredHint:
        'Un amministratore richiede il secondo fattore su questo account, quindi il server rifiuterà la rimozione — chiedi prima di togliere l’obbligo.',
      removeError: 'Impossibile rimuovere il secondo fattore'
    },
    profile: {
      menu: 'Vai al mio profilo',
      sessions: 'Sessioni attive',
      sessionsHint: 'Le tue sessioni aperte su questo nodo',
      sessionsEmpty: 'Nessuna sessione attiva',
      sessionCreated: 'Accesso effettuato',
      sessionExpires: 'Scade',
      revoke: 'Revoca sessione',
      revokeError: 'Impossibile revocare la sessione',
      loadError: 'Impossibile caricare le sessioni'
    }
  }
}
